import { useMemo } from "react";
import { useSelector } from "react-redux";
import { TextStyle, TextMetrics } from "pixi.js";
import { useDimensions } from "../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../textureConfigs";
import { PixiSettings } from "../pixiSettings";
import { FONT_CONFIG } from "../textureConfigs";
import { formatToCustomLocale } from "../utilities";
import { selectCurrency, selectBets } from "../State/selectors";

// Toolbar layout constraints (moved from pixiSettings)
const MAX_TOOLBAR_WIDTH = 2200;
const MAX_TOOLBAR_WIDTH_MOBILE_LANDSCAPE_MULTIPLIER = 12.5;
export const TOOLBAR_FADE_WIDTH = 150;

/**
 * Primary UI toolbar dimensions - for backport toolbars (ToolbarTop, ToolbarBottom).
 * Same as useToolbarDimensionsV2 except toolbar is ALWAYS visible - never disappears on orientation change.
 * Use this hook only with backport toolbar components.
 */
export const usePrimaryUIToolbarDimensionsV2 = () => {
  const {
    dimensions,
    device,
    orientation,
    getResponsiveConfig,
    uiSymbolSize,
    uiSymbolScale,
  } = useDimensions();
  const currency = useSelector(selectCurrency);
  const bets = useSelector(selectBets);

  // Memoize font multiplier calculation (used by both balance and bet width calculations)
  const fontMultiplier = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT ? 1.0 : 1.1;
    } else if (device === PixiSettings.DEVICE.TABLET) {
      return orientation === PixiSettings.ORIENTATION.PORTRAIT ? 1.65 : 1.25;
    } else {
      return 1.0; // Desktop
    }
  }, [device, orientation]);

  // Memoize button config
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Memoize max width calculation
  const maxWidth = useMemo(
    () =>
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.LANDSCAPE
        ? uiSymbolSize * MAX_TOOLBAR_WIDTH_MOBILE_LANDSCAPE_MULTIPLIER
        : MAX_TOOLBAR_WIDTH,
    [device, orientation, uiSymbolSize],
  );

  // Memoize width calculation
  const width = useMemo(
    () => Math.min(dimensions.width, maxWidth),
    [dimensions.width, maxWidth],
  );

  // Primary UI: toolbar always visible, never disappears on orientation change
  const height = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      return buttonConfig.height * 1.2;
    }
    if (device === PixiSettings.DEVICE.TABLET) {
      return buttonConfig.height * 1.85;
    }
    return buttonConfig.height * 1.85; // Desktop
  }, [device, buttonConfig.height]);

  // Memoize balance width calculation
  const balanceWidth = useMemo(() => {
    const baseFontSize = 32;
    const responsiveFontSize = baseFontSize * fontMultiplier;
    const idealFontSize = Math.round(responsiveFontSize * uiSymbolScale);

    // Calculate max balance width (7-digit balance)
    const maxBalance = 9999999;
    const maxBalanceText = `${formatToCustomLocale(maxBalance)} ${currency}`;
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "700",
    });
    const maxBalanceMetrics = TextMetrics.measureText(
      maxBalanceText,
      tempStyle,
    );

    return maxBalanceMetrics.width;
  }, [fontMultiplier, uiSymbolScale, currency]);

  // Memoize bet width calculation
  const betWidth = useMemo(() => {
    const baseFontSize = 32;
    const responsiveFontSize = baseFontSize * fontMultiplier;
    const fontSize = Math.round(responsiveFontSize * uiSymbolScale);

    // Calculate max bet width based on largest bet in bets array
    if (!bets || bets.length === 0) {
      return 100; // Fallback width
    }

    const maxBet = Math.max(...bets);
    const maxBetText = `${formatToCustomLocale(maxBet)} ${currency}`;
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: fontSize,
      fontWeight: "700",
    });
    const maxBetMetrics = TextMetrics.measureText(maxBetText, tempStyle);

    return maxBetMetrics.width;
  }, [fontMultiplier, uiSymbolScale, bets, currency]);

  // Memoize win display background width — single source of truth for both
  // WinDisplay background rendering and Balance/Bet positioning.
  // Uses WinDisplay's font sizing (base 36) and bgPaddingX (fontSize * 2.5).
  const winDisplayWidth = useMemo(() => {
    let winFontMultiplier = 1.0;
    let bgPaddingMultiplier = 1.5;
    if (device === PixiSettings.DEVICE.MOBILE) {
      winFontMultiplier =
        orientation === PixiSettings.ORIENTATION.PORTRAIT ? 1.05 : 1.1;
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        winFontMultiplier = 1.35;
        bgPaddingMultiplier = 1.5;
      } else {
        winFontMultiplier = 1.35;
      }
    }
    const winFontSize = Math.floor(36 * winFontMultiplier * uiSymbolScale);
    const bgPaddingX = winFontSize * bgPaddingMultiplier;
    // Measure a representative win text to get a stable content width
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: winFontSize,
      fontWeight: "700",
    });
    const metrics = TextMetrics.measureText("Win: 9.999,99 FUN", tempStyle);
    return metrics.width + bgPaddingX * 2;
  }, [device, orientation, uiSymbolScale]);

  return useMemo(() => {
    // Calculate positions based on device/orientation
    // Non-portrait: Balance and Bet flank the WinDisplay background in the center
    // Portrait: Balance far left, Bet far right (unchanged)
    const calculatePositions = () => {
      // Portrait layouts: keep Balance left, Bet right
      if (
        device === PixiSettings.DEVICE.MOBILE &&
        orientation === PixiSettings.ORIENTATION.PORTRAIT
      ) {
        return {
          balance: { x: buttonConfig.width * 0.55, width: balanceWidth },
          bet: { x: width - buttonConfig.width * 2.05, width: betWidth },
          winDisplay: { width: winDisplayWidth },
        };
      }

      // Non-mobile-portrait (tablet portrait, tablet landscape, mobile landscape, desktop):
      // Center-relative positioning — Balance | Win | Bet
      const toolbarCenter = width / 2;
      const gap = buttonConfig.width * 0.15;

      // Balance: left of win background (left-edge anchored)
      const balanceX = toolbarCenter - winDisplayWidth / 2 - balanceWidth - gap;

      // Bet: right of win background (center-anchored)
      // Full bet component width includes buttons: button + buttonGap + text + buttonGap + button
      // Using localized backport bet button size (BASE_WIDTH 45)
      const betButtonConfig = getResponsiveConfig(
        {
          BASE_WIDTH: 45,
          BASE_HEIGHT: 45,
          DEVICE_CONFIGS: {
            [PixiSettings.DEVICE.MOBILE]: {
              [PixiSettings.ORIENTATION.PORTRAIT]: { deviceMultiplier: 1.4 },
              [PixiSettings.ORIENTATION.LANDSCAPE]: { deviceMultiplier: 1.2 },
            },
            [PixiSettings.DEVICE.TABLET]: {
              [PixiSettings.ORIENTATION.PORTRAIT]: { deviceMultiplier: 1.2 },
              [PixiSettings.ORIENTATION.LANDSCAPE]: { deviceMultiplier: 1.2 },
            },
            [PixiSettings.DEVICE.DESKTOP]: { deviceMultiplier: 1.0 },
          },
        },
        true,
      );
      const buttonGap = betButtonConfig.width * 0.075;
      const fullBetWidth =
        betButtonConfig.width +
        buttonGap +
        betWidth +
        buttonGap +
        betButtonConfig.width;
      const betX = toolbarCenter + winDisplayWidth / 2 + fullBetWidth / 2 + gap;

      return {
        balance: { x: balanceX, width: balanceWidth },
        bet: { x: betX, width: betWidth },
        winDisplay: { width: winDisplayWidth },
      };
    };

    const layout = calculatePositions();

    return { width, height, layout };
  }, [
    width,
    height,
    balanceWidth,
    betWidth,
    buttonConfig,
    device,
    orientation,
    winDisplayWidth,
    getResponsiveConfig,
  ]);
};
